/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([], function () {

	/*
	 * A given VIPR visualization can be subdivided into different areas
	 * - and other API calls are affected by whether the coordinates in use
	 * straddled multiple areas. This class contains the logic for finding
	 * those areas, to inform how to make subsequent API calls. 
	 */
	class ViprAreaLocator {
		constructor({ viprWidget }) {
			this.viprWidget = viprWidget;

			this.bounds = this.viprWidget.domNode.getBoundingClientRect();
			this.idToArea = {};
			this.areaToExtents = {};
		}

		/*
		 * Search the corners of viprWidget to find areas. 
		 * Use getVisAreas() to access found areas.
		 */
		findAreasFromCorners() {
			//Search for areas in all four corners

			const left = 0,
				right = this.bounds.width - 1,
				top = 0,
				bottom = this.bounds.height - 1;


			this._findArea({ x: left,  y: top });
			this._findArea({ x: right, y: top });
			this._findArea({ x: left,  y: bottom });
			this._findArea({ x: right, y: bottom });
		}

		/*
		 * Use a selector to find DOM nodes inside viprWidget, and 
		 * use their bounds to try to find areas.
		 * Use getVisAreas() to access found areas.
		 */
		findAreasFromSelector(selector) {
			this.viprWidget.domNode.querySelectorAll(selector).forEach(
				node => this._findAreaFromNode(node)
			);
		}

		/*
		 * Get all areas found by this instance. 
		 * Each area has attributes `area` (id of area), `x`, `y`, `w` and `h`,
		 */
		getVisAreas() {
			return Object.keys(this.areaToExtents)
				.map(area => ({ ...this.areaToExtents[area], area }));
		}

		/*
		 * Get all areas matching a given type.
		 * Each returns area has attributes `type`, `offset` and `id`.
		 * @param {string} type - the type of area in question (type being an arbitrary string defined by VIDA)
		 */
		getAreasOfType(type) {
			return Object.keys(this.idToArea)
				.map(id => this.idToArea[id])
				.filter(area => area.type === type);
		}

		/*
		 * Given a DOM node, see if it is located on an area, and if so,
		 * find the bounds of that area and add the area to the internal list.
		 * Note: bounds of the area are truncated to those of the DOM node.
		 */
		_findAreaFromNode(n) {
			const bounds = n.getBoundingClientRect();

			const left = bounds.x - this.bounds.x,
				right = left + bounds.width,
				top = bounds.y - this.bounds.y,
				bottom = top + bounds.height;

			this._findArea({
				x: Math.floor((left + right) / 2),
				y: Math.floor((top + bottom) / 2),
				xMin: left,
				xMax: right,
				yMin: top,
				yMax: bottom
			});
		}

		/*
		 * Given a point, see if it is located on an area, and if so,
		 * find the bounds of that area and add the area to the internal list.
		 * Note: bounds of the area are truncated to those provided as parameters.
		 * If min/max parameters are not set, they default to the viprWidget's bounds.
		 */
		_findArea({
			x,
			y,
			xMin=0,
			yMin=0,
			xMax=this.bounds.width,
			yMax=this.bounds.height
		}) {
			const area = this._getAreaAt(x, y);

			if(area.type === 'none') {
				//No meaningful area found
				return;
			}

			if (this.idToArea[area.id]) {
				//Area has already been found - no more work required
				return;
			}

			const areaLeft = x <= xMin ? xMin : this._scan({
				init: x,
				limit: xMin,
				areaId: area.id,
				coords: i => ({ x:i, y })
			});

			const areaRight = x >= xMax ? xMax : this._scan({
				init: x,
				limit: xMax,
				areaId: area.id,
				coords: i => ({ x:i, y })
			});

			const areaTop = y <= yMin ? yMin : this._scan({
				init: y,
				limit: yMin,
				areaId: area.id,
				coords: i => ({ x, y: i })
			});

			const areaBottom = y >= yMax ? yMax : this._scan({
				init: y,
				limit: yMax,
				areaId: area.id,
				coords: i => ({ x, y: i })
			});

			//Save area info (id, type, extent)
			this.idToArea[area.id] = area;

			//Save extent info
			this.areaToExtents[area.id] = {
				x: areaLeft,
				y: areaTop,
				w: areaRight - areaLeft,
				h: areaBottom - areaTop
			};
		}

		/*
		 * Utility function to find the maximum extent of an area in one direction.
		 */
		_scan({ init, limit, areaId, coords }) {
			const isPositive = limit > init;
			const delta = isPositive ? 1 : -1;
			let i;
			for (
				i = init;
				isPositive ? i < limit : limit <= i;
				i += delta
			) {
				const { x, y } = coords(i);
				const area = this._getAreaAt(x, y);
				if (area.id !== areaId) {
					break;
				}
			}
			return i - delta;
		}

		/*
		 * Wrap the viprWidget.getVisCoordinate API to use the correct 
		 * coordinate system for VIPR
		 */
		_getAreaAt(x, y) {
			const {
				area, offset
			} = this.viprWidget.getVisCoordinate(
				this.bounds.x + x,
				this.bounds.y + y
			);

			return {
				type: area,
				offset,
				id: area + offset
			};
		}
	}

	return ViprAreaLocator;
});
